"use client";
import { useAppDispatch, useAppSelector } from "../../lib/redux/hooks";
import { changeTheme, selectTheme } from "../../lib/redux/settingsSlice";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const ThemeForm = () => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector(selectTheme);

  const handleThemeChange = (field: keyof typeof theme, value: string) => {
    dispatch(changeTheme({ field, value }));
  };

  const handleTemplateSelect = (templateName: string) => {
    switch (templateName) {
      case 'classic-blue':
        dispatch(changeTheme({ field: "fontFamily", value: "Inter" }));
        dispatch(changeTheme({ field: "fontSize", value: "12px" }));
        dispatch(changeTheme({ field: "documentSize", value: "Letter" }));
        dispatch(changeTheme({ field: "template", value: "classic-blue" }));
        break;
      case 'modern-gradient':
        dispatch(changeTheme({ field: "fontFamily", value: "Poppins" }));
        dispatch(changeTheme({ field: "fontSize", value: "11px" }));
        dispatch(changeTheme({ field: "documentSize", value: "A4" }));
        dispatch(changeTheme({ field: "template", value: "modern-gradient" }));
        break;
      default:
        break;
    }
  };

  return (
    <Card className="bg-white border-2 border-gray-800 shadow-lg">
      <CardHeader className="bg-gray-900 text-white">
        <CardTitle className="text-lg font-semibold">Resume Theme</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Template Selection */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Choose Template</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div 
            className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
              theme.template === 'classic-blue' 
                ? 'border-black bg-gray-100' 
                : 'border-gray-300 hover:border-black bg-white'
            }`}
            onClick={() => handleTemplateSelect('classic-blue')}
          >
            <div className="text-sm font-medium mb-2 text-black">Classic Blue</div>
            <div className="text-xs text-gray-600">Professional single-column layout with blue accents</div>
          </div>
          <div 
            className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
              theme.template === 'modern-gradient' 
                ? 'border-black bg-gray-100' 
                : 'border-gray-300 hover:border-black bg-white'
            }`}
            onClick={() => handleTemplateSelect('modern-gradient')}
          >
            <div className="text-sm font-medium mb-2 text-black">Modern Gradient</div>
            <div className="text-xs text-gray-600">Colorful design with gradients and cards</div>
          </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="font-family" className="text-black font-semibold">Font Family</Label>
            <Select
              value={theme.fontFamily}
              onValueChange={(value) => handleThemeChange("fontFamily", value)}
            >
                             <SelectTrigger className="border-2 border-gray-300 bg-white hover:border-black focus:border-black transition-colors text-black">
                 <SelectValue placeholder="Select font" />
               </SelectTrigger>
               <SelectContent className="bg-white border-2 border-black">
                 <SelectItem value="Inter" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Inter</SelectItem>
                 <SelectItem value="Roboto" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Roboto</SelectItem>
                 <SelectItem value="Open Sans" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Open Sans</SelectItem>
                 <SelectItem value="Lato" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Lato</SelectItem>
                 <SelectItem value="Poppins" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Poppins</SelectItem>
                 <SelectItem value="Montserrat" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Montserrat</SelectItem>
               </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="font-size" className="text-black font-semibold">Font Size</Label>
            <Select
              value={theme.fontSize}
              onValueChange={(value) => handleThemeChange("fontSize", value)}
            >
                             <SelectTrigger className="border-2 border-gray-300 bg-white hover:border-black focus:border-black transition-colors text-black">
                 <SelectValue placeholder="Select size" />
               </SelectTrigger>
               <SelectContent className="bg-white border-2 border-black">
                 <SelectItem value="10px" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Small (10px)</SelectItem>
                 <SelectItem value="11px" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Medium (11px)</SelectItem>
                 <SelectItem value="12px" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Large (12px)</SelectItem>
                 <SelectItem value="13px" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Extra Large (13px)</SelectItem>
                 <SelectItem value="14px" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">XXL (14px)</SelectItem>
               </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="document-size" className="text-black font-semibold">Document Size</Label>
            <Select
              value={theme.documentSize}
              onValueChange={(value) => handleThemeChange("documentSize", value)}
            >
                             <SelectTrigger className="border-2 border-gray-300 bg-white hover:border-black focus:border-black transition-colors text-black">
                 <SelectValue placeholder="Select size" />
               </SelectTrigger>
               <SelectContent className="bg-white border-2 border-black">
                 <SelectItem value="Letter" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Letter (US)</SelectItem>
                 <SelectItem value="A4" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">A4 (International)</SelectItem>
                 <SelectItem value="Legal" className="text-black hover:bg-gray-100 focus:bg-gray-100 focus:text-black data-[highlighted]:text-black data-[highlighted]:bg-gray-100">Legal</SelectItem>
               </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
